import React, { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Sprout, Layers, Compass, Rocket, ArrowRight } from 'lucide-react';

const STAGES = [
  {
    id: 'rookie',
    code: '01',
    label: 'ROOKIE',
    title: '素人期',
    icon: Sprout,
    pain: '想做內容，但不知道從哪開始。\n寫兩三條就沒靈感，發出去也沒反應。',
    quote: '「我到底是誰？我該對誰說話？」',
    role: '定位啟蒙',
    roleDesc: '透過問答幫你找到獨特定位與聲音，回答 5 個問題就能畫出你的創作座標。',
    accent: '#60A5FA',
  },
  {
    id: 'content',
    code: '02',
    label: 'CREATOR',
    title: '有內容期',
    icon: Layers,
    pain: '開始穩定發文，但流量忽高忽低。\n偶爾爆一條，之後又掉回原點。',
    quote: '「為什麼這條爆、那條不爆？沒人告訴我原因。」',
    role: '結構訓練',
    roleDesc: '爆款影片分析師幫你拆解爆款的底層結構，生成可被複製的腳本模板。',
    accent: '#FF5436',
  },
  {
    id: 'brand',
    code: '03',
    label: 'BRAND',
    title: '有定位期',
    icon: Compass,
    pain: '大家知道你是誰、你講什麼。\n粉絲在增長，但還沒變成生意。',
    quote: '「被看見了，但還沒被想起。」',
    role: '內容放大器',
    roleDesc: '批量產出符合定位的爆款變體，提高「被想起」的頻率，讓你在領域裡佔住位置。',
    accent: '#FF5436',
  },
  {
    id: 'scale',
    code: '04',
    label: 'SCALE',
    title: '有商業期',
    icon: Rocket,
    pain: '開始接業配、開課、做產品。\n但時間永遠不夠，創作變成瓶頸。',
    quote: '「事業做起來了，但我變成它的奴隸。」',
    role: '規模化引擎',
    roleDesc: '多代理協作體系，讓你 1 小時產出過去一週的內容量，把時間還給你自己。',
    accent: '#A3E635',
  },
];

const GrowthMap = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const [active, setActive] = useState(0);
  const stage = STAGES[active];
  const Icon = stage.icon;

  return (
    <section
      ref={ref}
      id="growth"
      className="relative py-24 md:py-32 border-y border-white/[0.06] overflow-hidden"
      data-testid="growth-map-section"
    >
      {/* Ambient glow */}
      <div
        className="absolute inset-0 pointer-events-none opacity-50"
        style={{
          background:
            'radial-gradient(ellipse 70% 50% at 50% 0%, rgba(255,84,54,0.06), transparent 70%)',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mb-16 md:mb-20"
        >
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-lime-500/60" />
            <span className="label-micro text-lime-500">GROWTH · PATH</span>
          </div>
          <h2 className="font-display font-semibold text-white tracking-tighter text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
            你在<span className="text-lime-500">這條路</span>的哪一段？
          </h2>
          <p className="text-obsidian-200 text-base md:text-lg mt-5 max-w-xl">
            每個創作者都會經過這 4 個階段。你卡在哪裡，Reelscript 就在哪裡接住你。
          </p>
        </motion.div>

        {/* Timeline navigator */}
        <div className="relative mb-12 md:mb-16">
          {/* Connecting path (desktop) */}
          <div
            aria-hidden
            className="hidden md:block absolute top-[28px] left-[10%] right-[10%] h-px pointer-events-none"
            style={{
              background:
                'linear-gradient(90deg, rgba(255,255,255,0.08) 0%, rgba(255,84,54,0.4) 50%, rgba(255,255,255,0.08) 100%)',
            }}
          />
          {/* Progress overlay */}
          <div
            aria-hidden
            className="hidden md:block absolute top-[28px] left-[10%] h-px bg-lime-500 pointer-events-none transition-all duration-700"
            style={{
              width: `${(active / (STAGES.length - 1)) * 80}%`,
              boxShadow: '0 0 12px rgba(255,84,54,0.5)',
            }}
          />

          <div className="relative grid grid-cols-4 gap-3 md:gap-6">
            {STAGES.map((s, i) => {
              const isActive = active === i;
              const isPassed = active > i;
              const StageIcon = s.icon;
              return (
                <button
                  key={s.id}
                  onClick={() => setActive(i)}
                  className="group flex flex-col items-center text-center"
                  data-testid={`growth-node-${s.id}`}
                >
                  {/* Node circle */}
                  <div
                    className={`relative w-14 h-14 rounded-full flex items-center justify-center transition-all duration-500 ${
                      isActive
                        ? 'bg-lime-500 border-2 border-lime-500 scale-110'
                        : isPassed
                        ? 'bg-lime-500/15 border-2 border-lime-500/50'
                        : 'bg-obsidian-900 border-2 border-white/10 group-hover:border-white/30'
                    }`}
                    style={{
                      boxShadow: isActive
                        ? '0 0 30px rgba(255,84,54,0.5)'
                        : undefined,
                    }}
                  >
                    <StageIcon
                      className={`w-5 h-5 transition-colors ${
                        isActive
                          ? 'text-obsidian-900'
                          : isPassed
                          ? 'text-lime-500'
                          : 'text-obsidian-300 group-hover:text-white'
                      }`}
                      strokeWidth={2}
                    />
                    {isActive && (
                      <span className="absolute inset-0 rounded-full border-2 border-lime-500 animate-ping opacity-50" />
                    )}
                  </div>

                  {/* Label */}
                  <div className="mt-4 md:mt-5">
                    <div
                      className={`label-micro transition-colors ${
                        isActive ? 'text-lime-500' : 'text-obsidian-400 group-hover:text-obsidian-200'
                      }`}
                    >
                      {s.code} · {s.label}
                    </div>
                    <div
                      className={`font-display text-sm md:text-base mt-1 font-semibold tracking-tight transition-colors ${
                        isActive ? 'text-white' : 'text-obsidian-300 group-hover:text-obsidian-100'
                      }`}
                    >
                      {s.title}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Active stage detail */}
        <AnimatePresence mode="wait">
          <motion.div
            key={stage.id}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5, ease: [0.25, 1, 0.5, 1] }}
            className="grid md:grid-cols-2 gap-px bg-white/[0.06] rounded-2xl overflow-hidden border border-white/[0.08]"
          >
            {/* LEFT: Pain / current state */}
            <div className="relative bg-obsidian-900 p-7 md:p-10 overflow-hidden">
              <div
                aria-hidden
                className="absolute -top-6 -left-4 font-serif font-bold text-white/[0.04] leading-none pointer-events-none select-none"
                style={{ fontSize: '200px' }}
              >
                {stage.code}
              </div>

              <div className="relative">
                <div className="flex items-center gap-3 mb-5">
                  <span className="label-micro text-obsidian-400">YOU · ARE · HERE</span>
                </div>
                <h3 className="font-display font-semibold text-white text-2xl md:text-3xl tracking-tight mb-5">
                  {stage.title}
                </h3>
                <p className="text-obsidian-200 text-sm md:text-base leading-relaxed whitespace-pre-line mb-8">
                  {stage.pain}
                </p>
                <div className="border-l-2 border-lime-500/60 pl-4">
                  <div className="label-micro text-lime-500 mb-1.5">INNER · VOICE</div>
                  <p className="font-serif italic text-white/90 text-base md:text-lg leading-relaxed">
                    {stage.quote}
                  </p>
                </div>
              </div>
            </div>

            {/* RIGHT: Reelscript role */}
            <div
              className="relative bg-obsidian-900 p-7 md:p-10 overflow-hidden"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 80% 10%, rgba(255,84,54,0.06), transparent 50%)',
              }}
            >
              <div className="relative">
                <div className="flex items-center justify-between mb-5">
                  <span className="label-micro text-lime-500">REELSCRIPT · STEPS · IN</span>
                  <div
                    className="w-10 h-10 bg-white/[0.04] border border-lime-500/30 rounded-md flex items-center justify-center text-lime-500"
                  >
                    <Icon className="w-4 h-4" strokeWidth={2} />
                  </div>
                </div>

                <h3 className="font-display font-semibold text-lime-500 text-2xl md:text-3xl tracking-tight mb-2">
                  {stage.role}
                </h3>
                <div className="h-px w-12 bg-lime-500/40 mb-5" />
                <p className="text-obsidian-200 text-sm md:text-base leading-relaxed mb-8">
                  {stage.roleDesc}
                </p>

                {/* Next stage hint */}
                {active < STAGES.length - 1 ? (
                  <button
                    onClick={() => setActive(active + 1)}
                    className="inline-flex items-center gap-2 text-sm text-white hover:text-lime-500 transition-colors group"
                    data-testid="growth-next-btn"
                  >
                    <span>前往下一階段：{STAGES[active + 1].title}</span>
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                  </button>
                ) : (
                  <button
                    onClick={() => setActive(0)}
                    className="inline-flex items-center gap-2 text-sm text-obsidian-400 hover:text-lime-500 transition-colors"
                  >
                    <span>重新檢視路徑</span>
                    <ArrowRight className="w-3.5 h-3.5 rotate-180" />
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default GrowthMap;
